/**
 * 119 확인 게이트 (EmergencyDispatch) 데이터 훅 — P4-A 통합.
 *
 * 심각(CRITICAL) 경보는 119를 자동 발신하지 않고, 관제요원 승인 카드를 띄운다.
 * 백엔드:
 *   * `GET  /api/v1/emergency-dispatches/?state=pending` — 대기 카드 목록
 *   * `POST /api/v1/emergency-dispatches/{id}/approve` — 승인(모의 119/CARIS 발신)
 *   * `POST /api/v1/emergency-dispatches/{id}/reject`  — 반려(미발신, 이력만)
 *
 * 목록은 WS(`emergency.gate.opened` / `emergency.gate.decided`, 경보 채널 공유)로
 * 즉시 무효화하고, 폴백으로 짧은 주기 폴링한다. 카운트다운 만료는 서버 스윕이
 * `emergency.gate.decided`(EXPIRED) 로 통지한다.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";
import { qk } from "../keys";

export type EmergencyDispatchState =
  | "pending"
  | "approved"
  | "sent"
  | "rejected"
  | "expired"
  | "failed";

/** 승인 시 어댑터로 넘어가는 119 발신 스냅샷(모의). */
export interface EmergencyCall119Payload {
  tts_script?: string;
  address?: string;
  gas?: string;
  concentration?: string | null;
  occupancy?: number | null;
  blueprint_url?: string | null;
  [k: string]: unknown;
}

export interface EmergencyDispatch {
  id: number;
  site: number;
  alert: number;
  gas: string;
  level: number;
  ri_at_open: number;
  state: EmergencyDispatchState;
  /** ISO — 이 시각 이후 미승인이면 자동 만료(미발신). */
  countdown_deadline: string;
  payload: { call_119?: EmergencyCall119Payload; caris?: Record<string, unknown> };
  result: { call_119?: Record<string, unknown>; caris?: Record<string, unknown> };
  requested_at: string;
  decided_at: string | null;
  decided_by: number | null;
  note: string;
}

interface DispatchList {
  count?: number;
  next: string | null;
  previous: string | null;
  results: EmergencyDispatch[];
}

/** 대기 중(PENDING) 119 발신 승인 카드. `enabled: false` = 폴링하지 않음(119 신고 대응을 끈 조직·권한 밖). */
export function useEmergencyDispatches(
  params?: { state?: EmergencyDispatchState; site?: number },
  opts?: { enabled?: boolean },
) {
  const p = { state: "pending" as EmergencyDispatchState, ...params };
  return useQuery({
    queryKey: qk.safety.emergency.list(p),
    enabled: opts?.enabled ?? true,
    queryFn: () =>
      api.get<DispatchList>("/emergency-dispatches/", { params: p }).then((r) => r.data),
    // WS 가 즉시 무효화하지만, 카운트다운 만료·연결 끊김 대비 폴백 폴링.
    refetchInterval: 15_000,
  });
}

/** 관제요원 승인 / 반려. */
export function useDecideDispatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      decision,
      note,
    }: {
      id: number;
      decision: "approve" | "reject";
      note?: string;
    }) =>
      api
        .post<EmergencyDispatch>(
          `/emergency-dispatches/${id}/${decision}/`,
          decision === "reject" ? { note: note ?? "" } : {},
        )
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.safety.emergency.all }),
  });
}
